import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../hooks/useAuth';

export default function OrganicResearchPage() {
  const [params] = useSearchParams();
  const [domain, setDomain] = useState(params.get('domain') || '');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('positions');
  const { updateCredits } = useAuth();

  async function analyze() {
    if (!domain.trim() || loading) return;
    setError(''); setLoading(true); setData(null);
    try {
      const res = await api.post('/domain/organic', { domain });
      setData(res.data.data);
      updateCredits(res.data.credits_remaining);
    } catch (e) { setError(e.response?.data?.error || 'Failed'); } finally { setLoading(false); }
  }

  useEffect(() => { if (params.get('domain')) analyze(); }, []);

  return (
    <div className="fade-up">
      <div className="page-header">
        <h1 className="page-title">Organic Research</h1>
        <p className="page-sub">All organic keywords, positions, pages and competitors for any domain · 4 credits</p>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <input className="input input-lg" placeholder="Enter domain e.g. nike.com" value={domain} onChange={e => setDomain(e.target.value)} onKeyDown={e => e.key === 'Enter' && analyze()} />
          <button className="btn btn-orange btn-lg" onClick={analyze} disabled={loading || !domain.trim()}>{loading ? 'Loading...' : 'Research →'}</button>
        </div>
        {error && <div style={{ marginTop: 10, color: '#FF3D57', fontSize: 13, background: '#FFEBEE', padding: '8px 12px', borderRadius: 6 }}>{error}</div>}
      </div>

      {loading && <div className="card"><div className="skeleton" style={{ height: 200 }} /></div>}

      {data && (
        <>
          <div className="metric-grid">
            {[
              { label: 'Organic Keywords', value: data.totalKeywords.toLocaleString(), sub: 'total ranking keywords' },
              { label: 'Top 3 Positions', value: `${data.positionDistribution.top3}%`, sub: 'keywords in top 3' },
              { label: 'Intent', value: `${data.intentDistribution.informational}% Info`, sub: 'informational dominant' },
              { label: 'Competitors', value: data.competitors.length, sub: 'main organic competitors' },
            ].map((m,i) => (
              <div key={i} className="card" style={{ margin: 0 }}>
                <div className="metric-label">{m.label}</div>
                <div className="metric-value">{m.value}</div>
                <div className="metric-sub">{m.sub}</div>
              </div>
            ))}
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
              <div><div style={{ fontSize: 11, color: '#6B7280', textTransform: 'uppercase', fontWeight: 700 }}>Top 3</div><div style={{ height: 6, width: 60, background: '#00C853', borderRadius: 3, marginTop: 4 }}></div><div style={{ fontSize: 11, marginTop: 4 }}>{data.positionDistribution.top3}%</div></div>
              <div><div style={{ fontSize: 11, color: '#6B7280', textTransform: 'uppercase', fontWeight: 700 }}>4-10</div><div style={{ height: 6, width: 60, background: '#2962FF', borderRadius: 3, marginTop: 4 }}></div><div style={{ fontSize: 11, marginTop: 4 }}>{data.positionDistribution.top4_10}%</div></div>
              <div><div style={{ fontSize: 11, color: '#6B7280', textTransform: 'uppercase', fontWeight: 700 }}>11-20</div><div style={{ height: 6, width: 60, background: '#FFAB00', borderRadius: 3, marginTop: 4 }}></div><div style={{ fontSize: 11, marginTop: 4 }}>{data.positionDistribution.top11_20}%</div></div>
              <div><div style={{ fontSize: 11, color: '#6B7280', textTransform: 'uppercase', fontWeight: 700 }}>21-50</div><div style={{ height: 6, width: 60, background: '#9CA3AF', borderRadius: 3, marginTop: 4 }}></div><div style={{ fontSize: 11, marginTop: 4 }}>{data.positionDistribution.top21_50}%</div></div>
              <div><div style={{ fontSize: 11, color: '#6B7280', textTransform: 'uppercase', fontWeight: 700 }}>51-100</div><div style={{ height: 6, width: 60, background: '#E5E7EB', borderRadius: 3, marginTop: 4 }}></div><div style={{ fontSize: 11, marginTop: 4 }}>{data.positionDistribution.top51_100}%</div></div>
            </div>
          </div>

          <div className="card">
            <div className="tabs">
              <button className={`tab-btn ${tab === 'positions' ? 'active' : ''}`} onClick={() => setTab('positions')}>Positions ({data.keywords.length})</button>
              <button className={`tab-btn ${tab === 'pages' ? 'active' : ''}`} onClick={() => setTab('pages')}>Top Pages ({data.topPages.length})</button>
              <button className={`tab-btn ${tab === 'competitors' ? 'active' : ''}`} onClick={() => setTab('competitors')}>Competitors</button>
            </div>

            {tab === 'positions' && (
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>Keyword</th><th>Pos.</th><th>Volume</th><th>KD</th><th>CPC</th><th>Traffic</th><th>Intent</th><th>URL</th></tr></thead>
                  <tbody>
                    {data.keywords.map((k,i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 600, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{k.keyword}</td>
                        <td><span className={`badge ${k.position <= 3 ? 'badge-green' : k.position <= 10 ? 'badge-blue' : k.position <= 20 ? 'badge-yellow' : 'badge-gray'}`}>{k.position}</span></td>
                        <td>{k.searchVolume.toLocaleString()}</td>
                        <td>{k.kd}%</td>
                        <td>${k.cpc}</td>
                        <td>{k.traffic}</td>
                        <td><span className="badge badge-gray" style={{ fontSize: 10 }}>{k.intent}</span></td>
                        <td style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11, color: '#6B7280' }}>{k.url}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {tab === 'pages' && (
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>Page</th><th>Keywords</th><th>Traffic</th><th>Traffic %</th></tr></thead>
                  <tbody>
                    {data.topPages.map((p,i) => (
                      <tr key={i}><td style={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>{p.url}</td><td>{p.keywords}</td><td>{p.traffic.toLocaleString()}</td><td>{p.trafficPercent}%</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {tab === 'competitors' && (
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>Domain</th><th>Common Keywords</th><th>SE Keywords</th><th>Traffic</th></tr></thead>
                  <tbody>
                    {data.competitors.map(c => (
                      <tr key={c.domain}><td style={{ fontWeight: 600 }}>{c.domain}</td><td>{c.commonKeywords.toLocaleString()}</td><td>{c.seKeywords.toLocaleString()}</td><td>{(c.traffic/1000).toFixed(1)}K</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
